import { debug, director, gfx, JsonAsset, renderer } from 'cc';
import IPreloader from './IPreloader';
import BundleAssetProvider, { TaskNamingHelper } from './BundleAssetProvider';
import LoadingTask from './Toolkit/Helper/Loading/LoadingTask/LoadingTask';
import { LoadingType } from './Config/AssetLoadingConfigType';
import { exportJsonFile } from './Toolkit/Utils/Utility';
import FrameActionExecutor from './Toolkit/Utils/FrameActionExecutor';

export type ShaderDefineMap = { [name: string]: { type: string | number | boolean } };

type BakedShaderData = {
    shaders: string[];
    defines: ShaderDefineMap;
};

const BAKED_SHADER_DATA_FILE_NAME = 'baked-shader-data';

class ShaderManager implements IPreloader {
    private _bakedShaderDataProvider: BundleAssetProvider = null;
    private _queuedShaderMap: { [programName: string]: string[] } = {};
    private _defineMap: ShaderDefineMap = {};
    private _loadingTask: LoadingTask = null;

    constructor(private readonly _targetBundleNames: string[] = []) {
        this._targetBundleNames.unshift('resources');
        // regenerate config to load baked shader data only
        this._bakedShaderDataProvider = new BundleAssetProvider(
            this._targetBundleNames.map((bundleName) => ({
                bundleName,
                uniqueID: 'shader',
                path: {
                    json: {
                        [`${bundleName}-${BAKED_SHADER_DATA_FILE_NAME}`]: BAKED_SHADER_DATA_FILE_NAME,
                    },
                },
            })),
        );

        const originDefine = renderer.programLib.define;
        const queueShaderMap = this._queuedShaderMap;
        const precompileShaders = this._precompileShaders.bind(this);
        renderer.programLib.define = function define(shader) {
            const result: renderer.IProgramInfo = originDefine.call(renderer.programLib, shader);
            const { name: programName } = result;
            // resolve queued shaders which are not being compiled
            if (queueShaderMap[programName]) {
                precompileShaders(queueShaderMap[programName]);
                delete queueShaderMap[programName];
            }
            return result;
        };
    }

    public preload() {
        if (!this._loadingTask) {
            this._loadingTask = new LoadingTask(
                TaskNamingHelper.concatTaskName({
                    loadingType: LoadingType.PRELOAD,
                    providerName: this._bakedShaderDataProvider.name,
                    configName: this._bakedShaderDataProvider.assetLoadingConfigs[0].name,
                    assetName: BAKED_SHADER_DATA_FILE_NAME,
                }),
                Promise.all(
                    this._targetBundleNames.map((bundleName) =>
                        // allow empty precompiled data
                        this._bakedShaderDataProvider.load(`${bundleName}-${BAKED_SHADER_DATA_FILE_NAME}`, JsonAsset).catch(() => null as JsonAsset),
                    ),
                ).then(async (jsonAssets) => {
                    const validJsonAssets = jsonAssets.filter(Boolean);
                    if (!validJsonAssets.length) {
                        return;
                    }
                    const shaderSet: Set<string> = new Set<string>();
                    const defines: ShaderDefineMap = {};
                    const bakedShaderData: BakedShaderData = { shaders: [], defines };
                    // merge all json data into one
                    jsonAssets.filter(Boolean).forEach((jsonAsset) => {
                        const data = jsonAsset.json as BakedShaderData;
                        data.shaders.forEach((shader) => shaderSet.add(shader));
                        Object.assign(defines, data.defines);
                    });
                    bakedShaderData.shaders = Array.from(shaderSet);
                    await this.precompileWithData(bakedShaderData);
                    this._targetBundleNames.map((bundleName) => this._bakedShaderDataProvider.clear(`${bundleName}-${BAKED_SHADER_DATA_FILE_NAME}`, JsonAsset));
                    jsonAssets.forEach((jsonAsset) => {
                        jsonAsset?.decRef(true);
                    });
                }),
            );
        }
        return this._loadingTask;
    }

    public bakeRendererShader(): void {
        // collect used defines
        const defineMap: ShaderDefineMap = {};
        // @ts-ignore
        const templates = renderer.programLib._templates;
        Object.values(templates).forEach((template: renderer.IProgramInfo) => {
            template.defines.forEach((define) => {
                if (!Object.prototype.hasOwnProperty.call(defineMap, define.name)) {
                    defineMap[define.name] = { type: define.type };
                }
            });
        });

        // @ts-ignore collect used shader name
        const shaderNames = Object.values(renderer.programLib._cache).map((shader) => shader.name);

        // export baked shader data
        exportJsonFile(JSON.stringify({ shaders: shaderNames.sort(), defines: defineMap }), BAKED_SHADER_DATA_FILE_NAME);
    }

    public precompileWithData(bakedShaderData: BakedShaderData) {
        Object.assign(this._defineMap, bakedShaderData.defines);
        return this._precompileShaders(bakedShaderData.shaders);
    }

    private _precompileShaders(shaders: string[]) {
        let index = 0;
        const executor = new FrameActionExecutor(
            () => {
                this._precompileShader(shaders[index]);
                index++;
            },
            {
                timesPerFrame: 1,
                totalTimes: shaders.length,
                dynamic: true,
            },
        );
        const promise = executor.start();
        return promise;
    }

    private _precompileShader(shaderName: string) {
        const params: string[] = shaderName.split('|');
        const programName: string = params.slice(0, 3).join('|');
        // queue shader if program has not loaded yet
        if (!renderer.programLib.hasProgram(programName)) {
            const queue = this._queuedShaderMap[programName] ?? (this._queuedShaderMap[programName] = []);
            queue.push(shaderName);
            return;
        }

        // parse defines
        const defines: renderer.MacroRecord = {};
        for (let i = 3; i < params.length; i++) {
            const param: string = params[i];
            const macros: string[] = param.match(/^(.*?)(\d+)$/);
            if (macros) {
                const define: string = macros[1];
                const value: string = macros[2];
                if (Object.prototype.hasOwnProperty.call(this._defineMap, define)) {
                    const { type } = this._defineMap[define];
                    if (type === 'boolean') {
                        defines[define] = value === '1';
                    } else if (type === 'number') {
                        defines[define] = +value;
                    }
                }
            }
        }

        const key = renderer.programLib.getKey(programName, defines);
        // @ts-ignore
        let shader: gfx.Shader = renderer.programLib._cache[key];
        if (!shader) {
            debug(`precompile shader: ${shaderName}`);
            shader = renderer.programLib.getGFXShader(director.root.device, programName, defines, director.root.pipeline, key);
        } else {
            debug(`shader ${shaderName} has been precompiled before`);
        }
        // @ts-ignore ensure WebGLCmdFuncCreateShader is called
        // eslint-disable-next-line no-unused-expressions
        (shader as any).gpuShader;
    }
}

export default ShaderManager;
